import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import CashierLayout from '../layouts/CashierLayout';
import { useAuth } from '../contexts/AuthContext';
import AuthGuard from '../contexts/AuthGuard';

// Cashier Module Pages
const Dashboard = lazy(() => import('../pages/modules/cashier/dashboard/Dashboard'));
const Delivery = lazy(() => import('../pages/modules/cashier/delivery/Delivery'));
const Takeaway = lazy(() => import('../pages/modules/cashier/takeaway/Takeaway'));
const Customers = lazy(() => import('../pages/modules/cashier/customers/Customers'));

// Menu View Pages
const MenuCategories = lazy(() => import('../pages/modules/cashier/menu-view/MenuCategories'));
const Subcategories = lazy(() => import('../pages/modules/cashier/menu-view/Subcategories'));
const Addons = lazy(() => import('../pages/modules/cashier/menu-view/Addons'));
const DiningTables = lazy(() => import('../pages/modules/cashier/menu-view/DiningTables'));
const TableOrder = lazy(() => import('../pages/modules/cashier/menu-view/TableOrder'));
const Sections = lazy(() => import('../pages/modules/cashier/menu-view/Sections'));

// Operations Pages
const Orders = lazy(() => import('../pages/modules/cashier/operations/Orders'));
const Payments = lazy(() => import('../pages/modules/cashier/operations/Payments'));
const Reports = lazy(() => import('../pages/modules/cashier/operations/Reports'));

// Outstanding Pages
const OutstandingDelivery = lazy(() => import('../pages/modules/cashier/outstanding/Delivery/OutstandingDelivery'));
const OutstandingHistory = lazy(() => import('../pages/modules/cashier/outstanding/OutstandingHistory/OutstandingHistory'));

// Wallet Topup
const WalletTopupRequest = lazy(() => import('../pages/modules/cashier/wallet-topup-request/WalletTopupRequest'));
const TopupHistory = lazy(() => import('../pages/modules/cashier/wallet-topup-request/TopupHistory'));

// Shared operations pages
const TablesManagement = lazy(() => import('../pages/modules/shared/TablesManagement/TablesManagement'));
const CustomerList = lazy(() => import('../pages/modules/shared/CustomerList/CustomerList'));

const RouteFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
  </div>
);

const CashierRoutes = () => {
  const { logout } = useAuth();

  return (
    <AuthGuard>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<CashierLayout onLogout={logout} />}>
            <Route path="/" element={<Navigate to="/cashier/dashboard" replace />} />
            <Route path="/cashier" element={<Navigate to="/cashier/dashboard" replace />} />

            {/* Dashboard */}
            <Route path="/cashier/dashboard" element={<Dashboard />} />

            {/* POS */}
            <Route path="/cashier/delivery" element={<Delivery />} />
            <Route path="/cashier/takeaway" element={<Takeaway />} />

            {/* Customers */}
            <Route path="/cashier/customers" element={<Customers />} />

            {/* Menu View (Read Only) */}
            <Route path="/cashier/menu-view/categories" element={<MenuCategories />} />
            <Route path="/cashier/menu-view/subcategories" element={<Subcategories />} />
            <Route path="/cashier/menu-view/addons" element={<Addons />} />
            <Route path="/cashier/dining-tables" element={<DiningTables />} />
            <Route path="/cashier/table-order/:tableId" element={<TableOrder />} />
            <Route path="/cashier/menu-view/sections" element={<Sections />} />

            {/* Operations */}
            <Route path="/cashier/operations/orders" element={<Orders />} />
            <Route path="/cashier/operations/payments" element={<Payments />} />
            <Route path="/cashier/operations/reports" element={<Reports />} />

            {/* Outstanding */}
            <Route path="/cashier/outstanding/delivery" element={<OutstandingDelivery />} />
            <Route path="/cashier/outstanding/history" element={<OutstandingHistory />} />

            {/* Wallet Topup */}
            <Route path="/cashier/wallet-topup/requests" element={<WalletTopupRequest />} />
            <Route path="/cashier/wallet-topup/history" element={<TopupHistory />} />

            {/* Operations — shared */}
            <Route path="/cashier/operations/tables" element={<TablesManagement scope="cashier" />} />
            <Route path="/cashier/operations/customer-list" element={<CustomerList scope="cashier" />} />

            <Route path="*" element={<Navigate to="/cashier/dashboard" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </AuthGuard>
  );
};

export default CashierRoutes;
